/**
 * Centralized i18n route map — Single Source of Truth for SEO international routing.
 *
 * EVERY public page must be registered here. The router, the language
 * switcher, every <SEO /> hreflang block, and the sitemap generator all
 * read from this list. Add a route here ONCE and the whole system stays
 * consistent. Never let an EN URL live under an ES slug or vice-versa.
 *
 * - ROUTE_PAIRS: canonical pairs surfaced in <hreflang> + sitemap.
 * - LEGACY_PAIRS: older slugs we still serve (for old bookmarks /
 *   inbound backlinks) but never advertise. Used only for language-
 *   toggle disambiguation.
 */

import type { Lang } from "./translations";

export interface LocalizedRoutePair {
  es: string;
  en: string;
}

export const ROUTE_PAIRS: LocalizedRoutePair[] = [
  // Home
  { es: "/", en: "/en" },
  // Secciones del panel público con URL limpia
  { es: "/agente-retail-ia", en: "/en/retail-ai-agent" },
  { es: "/que-es-el-agente-retail-ia", en: "/en/what-is-the-retail-ai-agent" },
  { es: "/consultas-resueltas", en: "/en/answers" },
  { es: "/hoja-de-ruta", en: "/en/retail-roadmap" },
  { es: "/herramientas/mis-locales", en: "/en/tools/my-premises" },
  { es: "/herramientas/proveedores", en: "/en/tools/suppliers" },
  { es: "/herramientas", en: "/en/tools" },
  // Free tools
  { es: "/herramientas/calculadora", en: "/en/tools/calculator" },
  { es: "/diagnostico-locales-comerciales", en: "/en/retail-location-analysis" },
  { es: "/detector-de-fugas", en: "/en/leak-detector" },
  // Services
  { es: "/mentoria-retail", en: "/en/mentoring" },
  // Landing sections promoted to standalone indexable pages
  { es: "/como-funciona", en: "/en/how-it-works" },
  { es: "/estrategia", en: "/en/strategy" },
  { es: "/todo-para-tu-comercio", en: "/en/everything-for-your-store" },
  { es: "/problemas-en-mi-tienda", en: "/en/store-problems" },
  { es: "/abrir-tienda-en-espana", en: "/en/open-a-store-in-spain" },
  { es: "/tarifas", en: "/en/pricing" },
  // Corporate solutions
  { es: "/cadenas-y-franquicias", en: "/en/chains-and-franchises" },
  { es: "/family-offices-inversores", en: "/en/family-offices-investors" },
  // Content
  { es: "/insights", en: "/en/insights" },
  { es: "/consejos", en: "/en/tips" },
  { es: "/casos-reales", en: "/en/real-cases" },
  { es: "/guias", en: "/en/guides" },
  // Landings de captación
  { es: "/lp/instagram", en: "/en/lp/instagram" },
  { es: "/lp/test-supervivencia", en: "/en/retail-survival-test" },
  // General
  { es: "/contacto", en: "/en/contact" },
  { es: "/faq", en: "/en/faq" },
  { es: "/recursos/subvenciones", en: "/en/resources/subsidies" },
  // Legal (footer)
  { es: "/aviso-legal", en: "/en/legal-notice" },
  { es: "/privacidad", en: "/en/privacy-policy" },
  { es: "/cookies", en: "/en/cookies" },
  { es: "/condiciones-contratacion", en: "/en/terms" },
];

// Older URLs we still serve. Recognised by the lang switcher and the
// URL→lang hydrator, but never emitted as canonical/hreflang.
const LEGACY_PAIRS: LocalizedRoutePair[] = [
  { es: "/agente-ia", en: "/en/ai-agent" },
  { es: "/herramientas/analisis-completo", en: "/en/tools/lemon-thermometer" },
  { es: "/mentoria", en: "/mentoring" },
  { es: "/calculadora-rentabilidad-retail", en: "/en/retail-profitability-calculator" },
  { es: "/termometro-lemon", en: "/en/lemon-thermometer" },
  { es: "/subvenciones", en: "/en/grants" },
  { es: "/termometro-lemon", en: "/lemon-thermometer" },
  { es: "/diagnosticador-locales", en: "/location-diagnosis" },
  { es: "/diagnostico-locales-comerciales", en: "/retail-location-analysis" },
  { es: "/calculadora-rentabilidad-retail", en: "/retail-profitability-calculator" },
  { es: "/aviso-legal", en: "/legal-notice" },
  { es: "/privacidad", en: "/privacy-policy" },
  { es: "/cookies", en: "/cookie-policy" },
  { es: "/condiciones-contratacion", en: "/terms-of-service" },
  { es: "/contacto", en: "/contact" },
];

/**
 * Rutas privadas (panel y cuenta). NO entran en el sitemap ni en hreflang:
 * solo existen para que el selector de idioma sepa a dónde navegar dentro de
 * la app sin perder la página en la que está la persona.
 */
export const APP_PAIRS: LocalizedRoutePair[] = [
  { es: "/dashboard", en: "/en/dashboard" },
  { es: "/panel/casos", en: "/en/panel/cases" },
  { es: "/panel/subvenciones", en: "/en/panel/grants" },
  { es: "/panel/calculadora-pro", en: "/en/panel/pro-calculator" },
  { es: "/cuenta/perfil", en: "/en/account/profile" },
  { es: "/cuenta/facturacion", en: "/en/account/billing" },
  { es: "/cuenta/presupuestos", en: "/en/account/quotes" },
  // Herramientas del panel sin par canónico propio: sin estas entradas el
  // selector de idioma no navegaba y la página volvía al idioma anterior.
  { es: "/herramientas/kpis", en: "/en/tools/kpis" },
  { es: "/herramientas/presupuestos", en: "/en/tools/proposals" },
  { es: "/marketing", en: "/en/marketing" },
  { es: "/musica-para-tu-local", en: "/en/music-for-your-store" },
  { es: "/publicaciones-para-tu-local", en: "/en/social-posts-for-your-store" },
  { es: "/aroma-para-tu-local", en: "/en/scent-for-your-store" },
];

const ALL_PAIRS = [...ROUTE_PAIRS, ...LEGACY_PAIRS, ...APP_PAIRS];


/**
 * Detect the language encoded in the URL itself.
 * Any path under `/en` or `/en/*` is English; any registered ES slug is
 * Spanish; unknown paths return null (caller keeps existing lang).
 */
export const getLangFromPath = (pathname: string): Lang | null => {
  if (pathname === "/en" || pathname.startsWith("/en/")) return "en";
  const pair = ALL_PAIRS.find((p) => p.es === pathname);
  if (pair) return "es";
  const en = ALL_PAIRS.find((p) => p.en === pathname);
  if (en) return "en";
  return null;
};

/** Find the canonical pair that owns this path (prefers ROUTE_PAIRS). */
export const findRoutePair = (pathname: string): LocalizedRoutePair | null => {
  return (
    ROUTE_PAIRS.find((p) => p.es === pathname || p.en === pathname) ||
    LEGACY_PAIRS.find((p) => p.es === pathname || p.en === pathname) ||
    null
  );
};

/**
 * Return the URL for the same page in the target language, or null.
 * Incluye las rutas privadas (APP_PAIRS) porque el selector de idioma también
 * debe funcionar dentro del panel; `findRoutePair` sigue siendo solo público
 * para que hreflang/canonical no expongan el área privada.
 */
export const getLocalizedPath = (pathname: string, target: Lang): string | null => {
  const pair =
    findRoutePair(pathname) ||
    APP_PAIRS.find((p) => p.es === pathname || p.en === pathname) ||
    null;
  if (!pair) return null;
  return pair[target];
};

/** Resolve a route key (e.g. "contact") to its localized path. */
export const ROUTE_KEYS = {
  home: { es: "/", en: "/en" },
  calc: { es: "/herramientas/calculadora", en: "/en/tools/calculator" },
  location: { es: "/diagnostico-locales-comerciales", en: "/en/retail-location-analysis" },
  thermometer: { es: "/detector-de-fugas", en: "/en/leak-detector" },
  mentoring: { es: "/mentoria-retail", en: "/en/mentoring" },
  everythingForShop: { es: "/todo-para-tu-comercio", en: "/en/everything-for-your-store" },
  chains: { es: "/cadenas-y-franquicias", en: "/en/chains-and-franchises" },
  familyOffices: { es: "/family-offices-inversores", en: "/en/family-offices-investors" },
  insights: { es: "/insights", en: "/en/insights" },
  contact: { es: "/contacto", en: "/en/contact" },
  faq: { es: "/faq", en: "/en/faq" },
  legal: { es: "/aviso-legal", en: "/en/legal-notice" },
  privacy: { es: "/privacidad", en: "/en/privacy-policy" },
  cookies: { es: "/cookies", en: "/en/cookies" },
  terms: { es: "/condiciones-contratacion", en: "/en/terms" },
} as const satisfies Record<string, LocalizedRoutePair>;

export type RouteKey = keyof typeof ROUTE_KEYS;

export const routeFor = (key: RouteKey, lang: Lang): string => ROUTE_KEYS[key][lang];
